import type { BadgeType } from '../components/shared/Badge';
import type { CanvasPage } from '../components/documentInspect/DocumentCanvasPanel';
import type { ReorderStatus } from '../types';

export type ChipKind =
    | 'provenance'
    | 'warning'
    | 'boundary'
    | 'reorder'
    | 'duplicate-internal'
    | 'duplicate-external'
    | 'notes';

export interface ChipSpec {
    key: string;
    kind: ChipKind;
    type: BadgeType;
    label?: string;
    title?: string;
}

const REORDER_LABEL: Record<ReorderStatus, string> = {
    backbone: 'Anchored',
    displaced: 'Displaced',
    isolated: 'Review',
    boundary: 'Boundary?',
    no_number: 'No page #',
};

/**
 * Derive the ordered chip list for a canvas page. Pure function: no rendering,
 * no DOM, no side effects. The single source of truth for which chips a page
 * shows and in what order — keep all conditional logic here so callers can
 * render via the shared Badge component without duplicating rules.
 *
 * Order: provenance → warnings → quality (boundary, reorder) → duplicates → activity.
 */
export const derivePageChips = (page: CanvasPage | null | undefined): ChipSpec[] => {
    if (!page) return [];

    const chips: ChipSpec[] = [];

    if (page.sourcePdf) {
        const pageSuffix = page.sourcePageIndex != null ? ` · p. ${page.sourcePageIndex + 1}` : '';
        chips.push({
            key: 'provenance',
            kind: 'provenance',
            type: 'info',
            label: `from ${page.sourcePdf}${pageSuffix}`,
            title: `Source: ${page.sourcePdf}${page.sourcePageIndex != null ? ` page ${page.sourcePageIndex + 1}` : ''}`,
        });
    }

    (page.warnings ?? []).forEach(warning => {
        chips.push({
            key: `warning:${warning.id}`,
            kind: 'warning',
            type: 'quality',
            label: warning.label,
        });
    });

    const conf = page.boundaryAfter?.confidence;
    if (conf != null && conf >= 0.4 && conf <= 0.6) {
        chips.push({
            key: 'boundary',
            kind: 'boundary',
            type: 'quality',
            label: 'Possible boundary',
        });
    }

    if (page.reorderStatus && page.reorderStatus !== 'backbone') {
        const printedSuffix = page.printedPageNumber != null ? ` (p${page.printedPageNumber})` : '';
        chips.push({
            key: 'reorder',
            kind: 'reorder',
            type: 'info',
            label: `${REORDER_LABEL[page.reorderStatus]}${printedSuffix}`,
        });
    }

    if (page.internalDuplicate) {
        chips.push({
            key: 'duplicate-internal',
            kind: 'duplicate-internal',
            type: 'duplicate',
            label: 'Duplicate (within doc)',
            title: 'Possible duplicate within this document',
        });
    }

    if (page.externalDuplicate) {
        chips.push({
            key: 'duplicate-external',
            kind: 'duplicate-external',
            type: 'duplicate',
            label: 'Duplicate',
            title: 'Possible duplicate in another document',
        });
    }

    const noteCount = page.notesCount ?? 0;
    if (noteCount > 0) {
        chips.push({
            key: 'notes',
            kind: 'notes',
            type: 'info',
            label: `Notes: ${noteCount}`,
        });
    }

    return chips;
};
